import React, { useEffect, useRef, useState } from 'react';
import _ from 'underscore';
import PropTypes from 'prop-types';
import { RubricTreePropType } from 'propTypes/common';
import Icon from 'components/Icon';
import { useDraggableScroll } from 'hooks/useDraggableScroll';
import { findRubricInTreeBySomeField } from 'utils/helpers/rubrics';
import { useIsMobileWithBreakpoint } from 'hooks/useIsMobileWithBreakpoint';
import RubricatorTag from './components/RubricatorTag';
import styles from './styles.module.scss';

const rubricatorButtonAllHash = {
	institutes: 'учреждений',
	religion_heritage: 'учреждений',
	culture_heritage: 'учреждений',
	persons: 'биографий'
};

const Rubricator = ({
	rubricsTree,
	setFilter,
	rootRubricPath,
	currentRubricPath
}) => {
	const isMobile = useIsMobileWithBreakpoint('tabletSmall');
	const ref = useRef(null);
	const { onMouseDown } = useDraggableScroll(ref, { direction: 'horizontal' });

	const [activeRubricId, setActiveRubricId] = useState(rubricsTree._id);

	const [currentRootRubric, setCurrentRootRubric] = useState(rubricsTree);

	const isRootRubric = currentRootRubric._id === rubricsTree._id;

	const selectRubric = (rubric) => {
		setActiveRubricId(rubric._id);
		const isRoot = rubric._id === rubricsTree._id;
		const rubricPath = isRoot ? '/' : rubric.path.replace(rootRubricPath, '');
		setFilter('rubricPath', rubricPath);
	};

	const handleRubricClick = (rubric) => {
		if (rubric._id === activeRubricId) {
			return;
		}
		selectRubric(rubric);
		const hasChildren = !_.isEmpty(rubric.children);
		const isCurrentRoot = currentRootRubric._id === rubric._id;

		if (hasChildren && !isCurrentRoot) {
			setCurrentRootRubric(rubric);
		}
	};

	const selectPreviousRubric = () => {
		if (currentRootRubric._id === rubricsTree._id) {
			return;
		}
		if (activeRubricId !== currentRootRubric._id) {
			selectRubric(currentRootRubric);
		} else {
			const rootRubric = findRubricInTreeBySomeField(
				rubricsTree,
				'_id',
				currentRootRubric.parentId
			);
			setCurrentRootRubric(rootRubric);
			selectRubric(rootRubric);
		}
	};

	const setRootRubric = () => {
		selectRubric(rubricsTree);
		setCurrentRootRubric(rubricsTree);
		setFilter('rubricPath', '/');
	};

	useEffect(() => {
		const currentRubric =
			findRubricInTreeBySomeField(rubricsTree, 'path', currentRubricPath) ||
			rubricsTree;
		setActiveRubricId(currentRubric._id);
		setCurrentRootRubric(
			_.isEmpty(currentRubric?.children)
				? findRubricInTreeBySomeField(
						rubricsTree,
						'_id',
						currentRubric.parentId
				  )
				: currentRubric
		);
	}, [currentRubricPath, rubricsTree]);

	useEffect(() => {
		if (ref && ref?.current && isMobile) {
			const active = Array.from(ref.current.children).find(
				(item) => item.dataset?.active === 'true'
			);
			ref.current.scrollTo({
				left: active.offsetLeft - ref.current.offsetLeft,
				behavior: 'smooth'
			});
		}
	}, [ref, activeRubricId]);

	const buttonAllTypeRubric = rubricatorButtonAllHash[rubricsTree?.name]
		? rubricatorButtonAllHash[rubricsTree?.name]
		: '';

	return (
		<div className={styles.Rubricator} {...{ onMouseDown, ref }}>
			<RubricatorTag
				title="Все"
				active={activeRubricId === rubricsTree._id}
				onClick={setRootRubric}
				ariaLabel={buttonAllTypeRubric}
				isButtonAll
			/>
			{!isRootRubric ? (
				<>
					<button
						className={styles.Rubricator__BackButton}
						onClick={selectPreviousRubric}
						type="button"
						aria-label="Вернуться на шаг назад"
					>
						<Icon
							icon="chevronLeftRounded"
							className={styles.Rubricator__BackIcon}
							aria-hidden="true"
						/>
					</button>

					{activeRubricId === currentRootRubric._id ? (
						<RubricatorTag
							title={currentRootRubric.title}
							active
							onClick={() => handleRubricClick(currentRootRubric)}
							hasMore={!_.isEmpty(currentRootRubric?.children)}
							ariaLabel={`Рубрика ${currentRootRubric.title}`}
						/>
					) : null}
				</>
			) : null}
			{currentRootRubric?.children?.map((rubric) => (
				<RubricatorTag
					title={rubric.title}
					onClick={() => handleRubricClick(rubric)}
					hasMore={!_.isEmpty(rubric.children)}
					active={activeRubricId === rubric._id}
					key={rubric._id}
					ariaLabel={`Рубрика ${rubric.title}`}
				/>
			))}
		</div>
	);
};

Rubricator.propTypes = {
	rubricsTree: RubricTreePropType.isRequired,
	setFilter: PropTypes.func.isRequired,
	rootRubricPath: PropTypes.string.isRequired,
	currentRubricPath: PropTypes.string
};

export default Rubricator;
